import React, { useState } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { downloadChatMedia, filenameFromMediaUrl } from "../../utils/downloadChatMedia";

/** Remove caracteres invisíveis que mantêm `message.text` truthy sem conteúdo visível. */
const stripForTranscription = (value) =>
  value == null ? "" : String(value).replace(/[\u200B-\u200D\uFEFF]/g, "").trim();

const TRANSCRIPTION_PLACEHOLDERS = new Set(
  [
    "[mensagem]",
    "[mensagem gupshup]",
    "[mensagem meta]",
    "[áudio]",
    "[audio]",
    "[áudio recebido]",
    "[audio recebido]",
    "áudio",
    "audio",
    "file_audio",
  ].map((s) => s.toLowerCase())
);

/** Texto de transcrição exibível: não vazio, não placeholder, não só a URL do áudio. */
export function getTranscriptionText(message, audioUrl) {
  if (!message) return "";
  if (message.transcription != null) {
    const t = stripForTranscription(message.transcription);
    if (t && !TRANSCRIPTION_PLACEHOLDERS.has(t.toLowerCase())) return t;
  }
  const fromText = stripForTranscription(message.text);
  if (!fromText) return "";
  if (TRANSCRIPTION_PLACEHOLDERS.has(fromText.toLowerCase())) return "";
  if (/^\[[^\]]{1,48}\]$/.test(fromText) && /áudio|audio|mídia|midia|mensagem/i.test(fromText)) {
    return "";
  }
  if (audioUrl && fromText === stripForTranscription(String(audioUrl))) return "";
  return fromText;
}

const AudioMessage = ({ message }) => {
  const { t } = useTranslation("chat");
  const [showTranscription, setShowTranscription] = useState(true);
  const theme = useTheme();
  const audioUrl = message?.audio || message?.url;
  if (!audioUrl || !String(audioUrl).trim()) return null;

  const transcriptionText = getTranscriptionText(message, audioUrl);

  const toggleTranscription = () => {
    setShowTranscription((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: "100%",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <audio
        controls
        style={{
          width: "100%",
          maxWidth: "100%",
          display: "block",
        }}
      >
        <source src={audioUrl} type="audio/mpeg" />
        Seu navegador não suporta áudio.
      </audio>
      <Button
        size="small"
        variant="text"
        onClick={() => downloadChatMedia(audioUrl, filenameFromMediaUrl(audioUrl, "audio.mp3"))}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          minWidth: 0,
          px: 0,
          py: 0,
          fontSize: "0.8rem",
        }}
      >
        {t("webchat.download")}
      </Button>
      {transcriptionText && (
        <Box>
          <Button
            size="small"
            variant="text"
            onClick={toggleTranscription}
            sx={{
              textTransform: "none",
              padding: 0,
              fontSize: "0.9rem",
              color: theme.palette.text.primary,
              "&:hover": {
                textDecoration: "underline",
                color: theme.palette.secondary.main,
              },
              "&:focus": {
                outline: "1px solid",
                outlineColor: theme.palette.primary.main,
              },
            }}
          >
            {showTranscription ? "Ocultar transcrição" : "Ver transcrição"}
          </Button>
          {showTranscription && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                marginTop: 1,
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
            >
              {transcriptionText}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AudioMessage;
